import React, { useState } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { useAuth } from '@pooflabs/web';
import { Time } from '@/lib/db-client';
import {
  subscribeManyFanArtSubmissions,
  FanArtSubmissionsResponse,
} from '@/lib/collections/fanArtSubmissions';
import {
  subscribeManyFanArtLikes,
  setFanArtLikes,
  deleteFanArtLikes,
  FanArtLikesResponse,
} from '@/lib/collections/fanArtLikes';
import {
  subscribeManyFanArtComments,
  setFanArtComments,
  FanArtCommentsResponse,
} from '@/lib/collections/fanArtComments';
import { toast } from 'sonner';
import { Heart, MessageCircle, ChevronDown, ChevronUp, Send, Image, ExternalLink } from 'lucide-react';

import { playBoing, playPop, playHover, playClick } from '@/utils/sounds';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatXHandle(value: string): string {
  // If it looks like a URL (contains x.com or twitter.com), extract the username
  try {
    const lower = value.toLowerCase();
    if (lower.includes('x.com/') || lower.includes('twitter.com/')) {
      const url = lower.includes('://') ? value : `https://${value}`;
      const pathname = new URL(url).pathname.replace(/\/$/, '');
      const username = pathname.split('/').filter(Boolean)[0];
      if (username) return `@${username}`;
    }
  } catch {}
  // Already an @handle or plain username
  return value.startsWith('@') ? value : `@${value}`;
}

function buildXProfileUrl(value: string): string {
  const lower = value.toLowerCase();
  if (lower.includes('x.com/') || lower.includes('twitter.com/')) {
    return value.startsWith('http') ? value : `https://${value}`;
  }
  const handle = value.startsWith('@') ? value.slice(1) : value;
  return `https://x.com/${handle}`;
}

function formatRelativeTime(unixSeconds: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const CHAT_NAME_KEY = 'dangerdan_chat_name';
const PURPLE = '#9B59B6';
const PURPLE_GLOW = 'rgba(155,89,182,0.25)';
const PURPLE_FAINT = 'rgba(155,89,182,0.08)';
const PURPLE_MID = 'rgba(155,89,182,0.35)';
const PURPLE_BORDER = 'rgba(155,89,182,0.2)';

// ─── Fan Art Comment Item ─────────────────────────────────────────────────────

const FanArtCommentItem: React.FC<{ comment: FanArtCommentsResponse }> = ({ comment }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-baseline gap-2">
      <span
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '0.85rem',
          letterSpacing: '0.06em',
          color: PURPLE,
        }}
      >
        {comment.displayName}
      </span>
      <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)' }}>
        {formatRelativeTime(comment.createdAt)}
      </span>
    </div>
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `2px solid ${PURPLE_BORDER}`,
        padding: '7px 10px',
        borderRadius: '0 6px 6px 6px',
        color: 'rgba(255,255,255,0.82)',
        fontSize: '0.82rem',
        lineHeight: 1.5,
        wordBreak: 'break-word',
      }}
    >
      {comment.message}
    </div>
  </div>
);

// ─── Fan Art Card ─────────────────────────────────────────────────────────────

const FAN_TILTS = [1.2, -1.5, 0.8, -1.8, 1.5, -0.9, 1.8, -1.2];

interface FanArtCardProps {
  submission: FanArtSubmissionsResponse;
  index: number;
  allLikes: FanArtLikesResponse[];
  allComments: FanArtCommentsResponse[];
}

const FanArtCard: React.FC<FanArtCardProps> = ({ submission, index, allLikes, allComments }) => {
  const tilt = FAN_TILTS[index % FAN_TILTS.length];
  // artType is stored as an extra field not yet in the generated type
  const artType = (submission as FanArtSubmissionsResponse & { artType?: string }).artType;
  const { user, login } = useAuth();
  const walletAddress = user?.address ?? null;

  // Optimistic like state: 'added' | 'removed' | null (null = follow subscription)
  const [optimisticLike, setOptimisticLike] = useState<'added' | 'removed' | null>(null);
  // Optimistic comments: appended immediately, removed once subscription confirms them
  const [optimisticComments, setOptimisticComments] = useState<FanArtCommentsResponse[]>([]);

  // Filter from subscription data
  const serverSubLikes = allLikes.filter(l => l.submissionId === submission.id);
  const serverSubComments = allComments.filter(c => c.submissionId === submission.id);

  const likeId = walletAddress ? `${walletAddress}_${submission.id}` : null;
  const serverHasLiked = likeId ? serverSubLikes.some(l => l.id === likeId) : false;

  // Reconcile optimistic like once subscription confirms the expected state
  React.useEffect(() => {
    if (optimisticLike === 'added' && serverHasLiked) setOptimisticLike(null);
    if (optimisticLike === 'removed' && !serverHasLiked) setOptimisticLike(null);
  }, [serverHasLiked, optimisticLike]);

  // Reconcile optimistic comments: remove ones confirmed by subscription
  React.useEffect(() => {
    if (optimisticComments.length === 0) return;
    const confirmedIds = new Set(serverSubComments.map(c => c.id));
    setOptimisticComments(prev => prev.filter(c => !confirmedIds.has(c.id)));
  }, [serverSubComments]);

  // Derive displayed values with optimistic overrides
  const hasLiked = optimisticLike === 'added' ? true : optimisticLike === 'removed' ? false : serverHasLiked;

  const optimisticLikeEntry: FanArtLikesResponse | null =
    optimisticLike === 'added' && likeId && walletAddress
      ? { id: likeId, submissionId: submission.id, walletAddress, createdAt: Math.floor(Date.now() / 1000), tarobase_created_at: Math.floor(Date.now() / 1000) }
      : null;

  const subLikesForDisplay = React.useMemo(() => {
    if (optimisticLike === 'added' && optimisticLikeEntry) {
      return serverSubLikes.some(l => l.id === likeId)
        ? serverSubLikes
        : [...serverSubLikes, optimisticLikeEntry];
    }
    if (optimisticLike === 'removed') {
      return serverSubLikes.filter(l => l.id !== likeId);
    }
    return serverSubLikes;
  }, [serverSubLikes, optimisticLike, optimisticLikeEntry, likeId]);

  // Merge optimistic comments with server comments, deduped by id, sorted newest-first
  const subComments = React.useMemo(() => {
    const confirmedIds = new Set(serverSubComments.map(c => c.id));
    const pendingComments = optimisticComments.filter(c => !confirmedIds.has(c.id));
    return [...serverSubComments, ...pendingComments].sort((a, b) => b.createdAt - a.createdAt);
  }, [serverSubComments, optimisticComments]);

  const likeCount = subLikesForDisplay.length;
  const commentCount = subComments.length;

  const [likePending, setLikePending] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentMessage, setCommentMessage] = useState('');
  const [commentName, setCommentName] = useState<string>(() => {
    try { return localStorage.getItem(CHAT_NAME_KEY) ?? ''; } catch { return ''; }
  });
  const [nameInput, setNameInput] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingComment, setPendingComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [imgError, setImgError] = useState(false);

  const handleLike = async () => {
    if (!walletAddress || !likeId || likePending) return;
    setLikePending(true);
    // Apply optimistic update immediately
    if (hasLiked) {
      playPop();
      setOptimisticLike('removed');
    } else {
      playBoing();
      setOptimisticLike('added');
    }
    try {
      if (hasLiked) {
        const ok = await deleteFanArtLikes(likeId);
        if (!ok) {
          setOptimisticLike(null);
          toast.error('Could not update like');
        }
      } else {
        const ok = await setFanArtLikes(likeId, {
          submissionId: submission.id,
          walletAddress,
          createdAt: Time.Now,
        });
        if (!ok) {
          setOptimisticLike(null);
          toast.error('Could not update like');
        }
      }
    } catch {
      setOptimisticLike(null);
      toast.error('Could not update like');
    } finally {
      setLikePending(false);
    }
  };

  const doPostComment = async (name: string, message: string) => {
    if (!walletAddress) return;
    if (!name.trim() || !message.trim()) return;
    setSendingComment(true);
    const id = crypto.randomUUID();
    const now = Math.floor(Date.now() / 1000);
    // Optimistically append the comment immediately
    const optimistic: FanArtCommentsResponse = {
      id,
      submissionId: submission.id,
      displayName: name.trim(),
      message: message.trim(),
      walletAddress: walletAddress ?? undefined,
      createdAt: now,
      tarobase_created_at: now,
    };
    setOptimisticComments(prev => [...prev, optimistic]);
    try {
      const ok = await setFanArtComments(id, {
        submissionId: submission.id,
        displayName: name.trim(),
        message: message.trim(),
        walletAddress: walletAddress ?? undefined,
        createdAt: Time.Now,
      });
      if (ok) {
        setCommentMessage('');
        playBoing();
      } else {
        setOptimisticComments(prev => prev.filter(c => c.id !== id));
        toast.error('Failed to post comment');
      }
    } catch {
      setOptimisticComments(prev => prev.filter(c => c.id !== id));
      toast.error('Failed to post comment');
    } finally {
      setSendingComment(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!commentMessage.trim()) return;
    if (!commentName) {
      setPendingComment(commentMessage);
      setShowNamePrompt(true);
      return;
    }
    await doPostComment(commentName, commentMessage);
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!nameInput.trim()) return;
    playPop();
    const name = nameInput.trim();
    try { localStorage.setItem(CHAT_NAME_KEY, name); } catch {}
    setCommentName(name);
    setShowNamePrompt(false);
    setNameInput('');
    if (pendingComment) {
      await doPostComment(name, pendingComment);
      setPendingComment('');
    }
  };

  return (
    <div
      className="fan-art-card"
      style={{
        background: 'linear-gradient(160deg, #0a0812 0%, #110e18 100%)',
        border: `4px solid ${PURPLE_MID}`,
        boxShadow: `5px 5px 0 rgba(0,0,0,0.7), 0 0 0 1px ${PURPLE_BORDER}`,
        transform: `rotate(${tilt}deg)`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'default',
        overflow: 'hidden',
        breakInside: 'avoid',
        marginBottom: '1.5rem',
        opacity: 0,
        animationDelay: `${index * 0.08}s`,
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.transform = `rotate(${tilt * 0.3}deg) translateY(-4px)`;
        el.style.boxShadow = `8px 8px 0 rgba(0,0,0,0.8), 0 0 25px ${PURPLE_GLOW}`;
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.transform = `rotate(${tilt}deg)`;
        el.style.boxShadow = `5px 5px 0 rgba(0,0,0,0.7), 0 0 0 1px ${PURPLE_BORDER}`;
      }}
    >
      {/* Image */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#060410' }}>
        {!imgError ? (
          <img
            src={submission.imageUrl}
            alt={submission.title ?? `Fan art by ${submission.artistName}`}
            loading="lazy"
            onError={() => setImgError(true)}
            style={{ width: '100%', display: 'block', maxHeight: '300px', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '180px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.2)',
            }}
          >
            <Image size={32} />
          </div>
        )}

        {/* FAN ART badge — top-left */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
          }}
        >
          <div
            style={{
              background: PURPLE,
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              padding: '2px 8px',
              border: '2px solid rgba(0,0,0,0.6)',
              boxShadow: '2px 2px 0 rgba(0,0,0,0.5)',
            }}
          >
            FAN ART
          </div>
        </div>

        {/* AI mark — top-right, only for AI art */}
        {artType === 'ai' && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(56,189,248,0.3)',
              borderRadius: '4px',
              padding: '2px 5px',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
            }}
          >
            <span style={{ fontSize: '11px', lineHeight: 1 }}>🤖</span>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '10px',
                letterSpacing: '0.05em',
                color: 'rgba(56,189,248,0.85)',
                lineHeight: 1,
              }}
            >
              AI
            </span>
          </div>
        )}

      </div>

      {/* Info */}
      <div style={{ padding: '10px 14px 8px' }}>
        {/* Artist name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: submission.xProfile ? '3px' : '4px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '0.9rem',
              letterSpacing: '0.08em',
              color: PURPLE,
              textShadow: `1px 1px 0 rgba(0,0,0,0.6)`,
            }}
          >
            {submission.artistName}
          </span>
          {submission.xLink && (
            <a
              href={submission.xLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                color: 'rgba(29,161,242,0.8)',
                fontSize: '0.7rem',
                textDecoration: 'none',
                fontFamily: 'monospace',
                border: '1px solid rgba(29,161,242,0.25)',
                padding: '1px 6px',
                borderRadius: '3px',
                transition: 'color 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(29,161,242,1)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(29,161,242,0.5)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(29,161,242,0.8)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(29,161,242,0.25)';
              }}
            >
              <ExternalLink size={10} />
              X
            </a>
          )}
        </div>
        {/* X Profile — prominent clickable link */}
        {submission.xProfile && (
          <div style={{ marginBottom: '4px' }}>
            <a
              href={buildXProfileUrl(submission.xProfile)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                color: 'rgba(29,161,242,0.95)',
                fontSize: '0.8rem',
                textDecoration: 'none',
                fontFamily: 'monospace',
                fontWeight: 600,
                letterSpacing: '0.02em',
                background: 'rgba(29,161,242,0.08)',
                border: '1px solid rgba(29,161,242,0.3)',
                padding: '2px 8px 2px 5px',
                borderRadius: '4px',
                transition: 'color 0.15s ease, background 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = 'rgba(29,161,242,1)';
                el.style.background = 'rgba(29,161,242,0.15)';
                el.style.borderColor = 'rgba(29,161,242,0.55)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = 'rgba(29,161,242,0.95)';
                el.style.background = 'rgba(29,161,242,0.08)';
                el.style.borderColor = 'rgba(29,161,242,0.3)';
              }}
            >
              {/* X/Twitter icon */}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              {formatXHandle(submission.xProfile)}
            </a>
          </div>
        )}
        {submission.title && (
          <h3
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.1rem',
              letterSpacing: '0.06em',
              color: '#ffffff',
              textShadow: '1px 1px 0 rgba(0,0,0,0.6)',
              marginBottom: submission.description ? '4px' : 0,
            }}
          >
            {submission.title}
          </h3>
        )}
        {submission.description && (
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.8rem',
              lineHeight: 1.5,
            }}
          >
            {submission.description}
          </p>
        )}
      </div>

      {/* Like & Comment bar */}
      <div
        style={{
          borderTop: `2px solid ${PURPLE_BORDER}`,
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(0,0,0,0.25)',
        }}
      >
        {/* Like button */}
        <button
          onClick={handleLike}
          disabled={!walletAddress || likePending}
          title={walletAddress ? (hasLiked ? 'Unlike' : 'Like this') : 'Connect wallet to like'}
          onMouseEnter={playHover}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            background: hasLiked ? 'rgba(155,89,182,0.15)' : 'rgba(255,255,255,0.04)',
            border: hasLiked ? `2px solid ${PURPLE_MID}` : '2px solid rgba(255,255,255,0.1)',
            color: hasLiked ? PURPLE : 'rgba(255,255,255,0.4)',
            padding: '4px 10px',
            cursor: walletAddress && !likePending ? 'pointer' : 'default',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.85rem',
            letterSpacing: '0.08em',
            transition: 'all 0.15s ease',
            boxShadow: hasLiked ? `0 0 12px ${PURPLE_GLOW}` : 'none',
            opacity: likePending ? 0.6 : 1,
          }}
        >
          <Heart
            size={13}
            fill={hasLiked ? PURPLE : 'none'}
            stroke={hasLiked ? PURPLE : 'rgba(255,255,255,0.4)'}
            strokeWidth={2.5}
          />
          <span>{likeCount > 0 ? likeCount : ''}</span>
          <span style={{ fontSize: '0.75rem' }}>{hasLiked ? 'LIKED' : 'LIKE'}</span>
        </button>

        {/* Comment toggle */}
        <button
          onClick={() => { playClick(); setCommentsOpen(v => !v); }}
          onMouseEnter={playHover}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            background: commentsOpen ? PURPLE_FAINT : 'rgba(255,255,255,0.04)',
            border: commentsOpen ? `2px solid ${PURPLE_MID}` : '2px solid rgba(255,255,255,0.1)',
            color: commentsOpen ? PURPLE : 'rgba(255,255,255,0.4)',
            padding: '4px 10px',
            cursor: 'pointer',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.85rem',
            letterSpacing: '0.08em',
            transition: 'all 0.15s ease',
          }}
        >
          <MessageCircle size={13} strokeWidth={2.5} />
          <span>{commentCount > 0 ? commentCount : ''}</span>
          <span style={{ fontSize: '0.75rem' }}>
            {commentCount === 1 ? 'COMMENT' : 'COMMENTS'}
          </span>
          {commentsOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>

      {/* Comments panel */}
      {commentsOpen && (
        <div
          style={{
            background: '#06030e',
            borderTop: `2px solid ${PURPLE_BORDER}`,
            padding: '12px 14px 14px',
            animation: 'fanart-comments-slide-in 0.25s ease-out forwards',
          }}
        >
          {subComments.length === 0 ? (
            <p
              style={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.2)',
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.1em',
                textAlign: 'center',
                padding: '8px 0 10px',
              }}
            >
              No comments yet — be first!
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                maxHeight: '220px',
                overflowY: 'auto',
                marginBottom: '12px',
                paddingRight: '4px',
                scrollbarWidth: 'thin',
                scrollbarColor: `${PURPLE_MID} transparent`,
              }}
            >
              {subComments.map(c => (
                <FanArtCommentItem key={c.id} comment={c} />
              ))}
            </div>
          )}

          <div style={{ height: '1px', background: PURPLE_BORDER, marginBottom: '10px' }} />

          {!walletAddress ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
                Connect wallet to comment
              </p>
              <button
                onClick={() => login()}
                onMouseEnter={playHover}
                style={{
                  background: PURPLE,
                  color: '#fff',
                  border: '2px solid rgba(0,0,0,0.5)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.6)',
                  padding: '5px 12px',
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.85rem',
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'transform 0.1s ease',
                }}
              >
                Connect Wallet
              </button>
            </div>
          ) : showNamePrompt ? (
            <form onSubmit={handleNameSubmit} style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1 }}>
                <input
                  autoFocus
                  type="text"
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  placeholder="Pick a display name..."
                  maxLength={24}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.06)',
                    border: `2px solid ${PURPLE_MID}`,
                    color: '#fff',
                    padding: '7px 10px',
                    outline: 'none',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    boxShadow: `0 0 8px ${PURPLE_GLOW}`,
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!nameInput.trim()}
                onMouseEnter={playHover}
                style={{
                  background: PURPLE,
                  color: '#fff',
                  border: '2px solid rgba(0,0,0,0.5)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.6)',
                  padding: '7px 12px',
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.85rem',
                  letterSpacing: '0.08em',
                  cursor: nameInput.trim() ? 'pointer' : 'not-allowed',
                  opacity: nameInput.trim() ? 1 : 0.5,
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-start',
                }}
              >
                Set Name
              </button>
            </form>
          ) : (
            <form onSubmit={handleCommentSubmit} style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={commentMessage}
                onChange={e => setCommentMessage(e.target.value)}
                placeholder="Leave a comment..."
                maxLength={280}
                disabled={sendingComment}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  padding: '7px 10px',
                  outline: 'none',
                  fontSize: '0.8rem',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = PURPLE_MID; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
              />
              <button
                type="submit"
                disabled={sendingComment || !commentMessage.trim()}
                onMouseEnter={playHover}
                style={{
                  background: sendingComment || !commentMessage.trim() ? `${PURPLE_FAINT}` : PURPLE,
                  color: '#fff',
                  border: '2px solid rgba(0,0,0,0.4)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.5)',
                  padding: '7px 10px',
                  cursor: sendingComment || !commentMessage.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s ease',
                  flexShrink: 0,
                }}
              >
                <Send size={14} />
              </button>
            </form>
          )}

          {commentName && !showNamePrompt && (
            <div style={{ marginTop: '6px' }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)' }}>
                Commenting as{' '}
                <button
                  onClick={() => {
                    playPop();
                    setCommentName('');
                    try { localStorage.removeItem(CHAT_NAME_KEY); } catch {}
                  }}
                  onMouseEnter={playHover}
                  style={{
                    color: PURPLE,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: "'Bangers', cursive",
                    fontSize: '0.85rem',
                    letterSpacing: '0.05em',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    padding: 0,
                  }}
                >
                  {commentName}
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Fan Art Section ─────────────────────────────────────────────────────

export const FanArtSection: React.FC = () => {
  // Load all approved submissions
  const { data: allSubmissions } = useRealtimeData<FanArtSubmissionsResponse[]>(
    subscribeManyFanArtSubmissions,
    true,
    "where status = 'approved'"
  );

  const { data: allLikesData } = useRealtimeData<FanArtLikesResponse[]>(
    subscribeManyFanArtLikes,
    true,
  );
  const { data: allCommentsData } = useRealtimeData<FanArtCommentsResponse[]>(
    subscribeManyFanArtComments,
    true,
  );

  const approvedSubs = (allSubmissions ?? []).sort((a, b) => b.submittedAt - a.submittedAt);
  const allLikes = allLikesData ?? [];
  const allComments = allCommentsData ?? [];

  return (
    <section className="relative py-16 px-6">
      {/* Section divider */}
      <div
        style={{
          height: '3px',
          background: `linear-gradient(90deg, transparent, ${PURPLE} 30%, rgba(155,89,182,0.4) 50%, ${PURPLE} 70%, transparent)`,
          marginBottom: '48px',
          opacity: 0.5,
        }}
      />

      {/* Section header */}
      <div className="container mb-10 flex flex-col items-center">
        <div
          style={{
            display: 'inline-block',
            background: PURPLE,
            color: '#fff',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.8rem',
            letterSpacing: '0.2em',
            padding: '2px 14px',
            border: '3px solid rgba(0,0,0,0.7)',
            boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
            transform: 'rotate(-1.2deg)',
            marginBottom: '12px',
            userSelect: 'none',
          }}
        >
          COMMUNITY
        </div>
        <h2
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: 'clamp(2rem, 7vw, 4rem)',
            letterSpacing: '0.1em',
            color: '#ffffff',
            textShadow: `4px 4px 0 rgba(0,0,0,0.8), 0 0 30px ${PURPLE_GLOW}`,
            textAlign: 'center',
          }}
        >
          FAN ART
        </h2>
        <div
          style={{
            height: '3px',
            width: '80px',
            background: `linear-gradient(90deg, ${PURPLE}, rgba(155,89,182,0.3))`,
            marginTop: '8px',
          }}
        />
      </div>

      <div className="container">
        {/* Gallery */}
        {approvedSubs.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 24px',
              border: `3px dashed ${PURPLE_BORDER}`,
              background: PURPLE_FAINT,
              textAlign: 'center',
            }}
          >
            <Image size={36} style={{ color: `rgba(155,89,182,0.3)`, marginBottom: '12px' }} />
            <p
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '1.3rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.2)',
                marginBottom: '6px',
              }}
            >
              No fan art yet
            </p>
            <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.82rem' }}>
              Fan art coming soon!
            </p>
          </div>
        ) : (
          <div
            style={{
              columns: 'auto',
              columnCount: 2,
              columnGap: '1.25rem',
            }}
            className="fan-art-masonry"
          >
            {approvedSubs.map((sub, i) => (
              <FanArtCard
                key={sub.id}
                submission={sub}
                index={i}
                allLikes={allLikes}
                allComments={allComments}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .fan-art-masonry {
          columns: 1;
          column-gap: 1.25rem;
        }
        @media (min-width: 480px) { .fan-art-masonry { columns: 2; } }
        @media (min-width: 1024px) { .fan-art-masonry { columns: 2; } }
        .fan-art-card {
          animation: fanart-fade-in 0.5s ease-out forwards;
        }
        @keyframes fanart-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fanart-comments-slide-in {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default FanArtSection;
